import type { AppData, Manifest, NavigationData } from "../types";

const APP_CACHE_KEY = "makanmane:data:v1";
const NAV_CACHE_KEY = "makanmane:navigation:v1";

interface DataCache {
  version: string;
  savedAt: string;
  data: AppData;
}

interface NavigationCache {
  version: string;
  savedAt: string;
  data: NavigationData;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }
}

function writeJson<T>(key: string, value: T): void {
  window.localStorage.setItem(key, JSON.stringify(value));
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { cache: "no-cache" });
  if (!response.ok) {
    throw new Error(`Unable to load ${url}`);
  }
  return (await response.json()) as T;
}

export async function loadAppData(): Promise<AppData> {
  const manifest = await fetchJson<Manifest>("/data/manifest.json");
  const cached = readJson<DataCache>(APP_CACHE_KEY);

  if (cached?.version === manifest.dataVersion) {
    return cached.data;
  }

  const [shops, categories, facilities] = await Promise.all([
    fetchJson<AppData["shops"]>(manifest.shopsUrl),
    fetchJson<AppData["categories"]>(manifest.categoriesUrl),
    fetchJson<AppData["facilities"]>(manifest.facilitiesUrl)
  ]);

  const data: AppData = {
    manifest,
    shops: shops.filter((shop) => shop.isActive),
    categories: [...categories].sort((a, b) => a.sortOrder - b.sortOrder),
    facilities
  };

  writeJson<DataCache>(APP_CACHE_KEY, {
    version: manifest.dataVersion,
    savedAt: new Date().toISOString(),
    data
  });

  return data;
}

export async function loadNavigationData(manifest: Manifest): Promise<NavigationData> {
  const cached = readJson<NavigationCache>(NAV_CACHE_KEY);

  if (cached?.version === manifest.dataVersion) {
    return cached.data;
  }

  const base = manifest.navigationBaseUrl;
  const [floors, nodes, edges, shopPoints, landmarks] = await Promise.all([
    fetchJson<NavigationData["floors"]>(`${base}floors.json`),
    fetchJson<NavigationData["nodes"]>(`${base}map-nodes.json`),
    fetchJson<NavigationData["edges"]>(`${base}map-edges.json`),
    fetchJson<NavigationData["shopPoints"]>(`${base}shop-map-points.json`),
    fetchJson<NavigationData["landmarks"]>(`${base}landmarks.json`)
  ]);

  const data: NavigationData = { floors, nodes, edges, shopPoints, landmarks };
  writeJson<NavigationCache>(NAV_CACHE_KEY, {
    version: manifest.dataVersion,
    savedAt: new Date().toISOString(),
    data
  });

  return data;
}

export function clearLocalData(): void {
  window.localStorage.removeItem(APP_CACHE_KEY);
  window.localStorage.removeItem(NAV_CACHE_KEY);

  if ("caches" in window) {
    caches.keys().then((keys) => {
      keys.filter((key) => key.startsWith("makanmane-")).forEach((key) => caches.delete(key));
    });
  }
}

